import { useEffect, useState } from "react";
import {
  createAboutUs,
  getAboutUs,
  updateAboutUs,
} from "../../../api/apiServices";

export default function AboutUsSection() {
  const [about, setAbout] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [founderImageFile, setFounderImageFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [exists, setExists] = useState(false);

  const emptyAbout = {
    title: "",
    description: "",
    mission: "",
    vision: "",
    image: "",
    founder: {
      name: "",
      role: "",
      quote: "",
      bio: "",
      image: "",
    },
    ourWork: {
      title: "",
      description: "",
      items: [],
    },
    isPublished: true,
  };

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const res = await getAboutUs();

      if (res?.data) {
        setAbout(res.data);
        setExists(true);
      } else {
        setAbout(emptyAbout);
        setExists(false);
      }
    } catch (err) {
      console.error("Failed to fetch about info", err);
      setAbout(emptyAbout);
      setExists(false);
    }
  };

  /* ---------------- SAVE ---------------- */
  const handleSave = async () => {
    try {
      setIsSaving(true);

      const formData = new FormData();

      // Main About
      formData.append("title", about.title);
      formData.append("description", about.description);
      formData.append("mission", about.mission);
      formData.append("vision", about.vision);
      formData.append("isPublished", String(about.isPublished));

      // Founder
      formData.append("founderName", about.founder.name);
      formData.append("founderRole", about.founder.role);
      formData.append("founderQuote", about.founder.quote);
      formData.append("founderBio", about.founder.bio);

      // Our Work
      formData.append("ourWorkTitle", about.ourWork.title);
      formData.append("ourWorkDescription", about.ourWork.description);
      formData.append(
        "ourWorkItems",
        JSON.stringify(about.ourWork.items)
      );

      // Images
      if (imageFile) formData.append("image", imageFile);
      if (founderImageFile)
        formData.append("founderImage", founderImageFile);

      if (exists) {
        await updateAboutUs(about._id, formData);
      } else {
        const res = await createAboutUs(formData);
        setAbout(res.data);
        setExists(true);
      }

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setIsSaving(false);
    }
  };

  const addWorkItem = () => {
    setAbout({
      ...about,
      ourWork: {
        ...about.ourWork,
        items: [...about.ourWork.items, { title: "", description: "" }],
      },
    });
  };

  const updateWorkItem = (index, field, value) => {
    const items = [...about.ourWork.items];
    items[index][field] = value;
    setAbout({
      ...about,
      ourWork: { ...about.ourWork, items },
    });
  };

  const removeWorkItem = (index) => {
    const items = about.ourWork.items.filter((_, i) => i !== index);
    setAbout({
      ...about,
      ourWork: { ...about.ourWork, items },
    });
  };

  if (!about) return null;

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            About Us
          </h2>
          <p className="text-gray-600 text-sm">
            Manage your organization’s story
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={isSaving}
          className="bg-[#6a4bc3] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#5a3ba3] disabled:opacity-50 flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Saving...
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Save Changes
            </>
          )}
        </button>
      </div>

      {showSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
          <i className="ri-check-line"></i>
          About section saved successfully!
        </div>
      )}

      {/* MAIN CONTENT */}
      <div className="bg-white rounded-xl p-6 shadow-sm border space-y-8">

        {/* COVER IMAGE */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Cover Image
          </label>
          {about.image && (
            <img
              src={about.image}
              alt="About"
              className="w-full max-h-64 object-cover rounded-lg mb-3"
            />
          )}
          <input type="file" onChange={(e) => setImageFile(e.target.files[0])} />
        </div>

        {/* BASIC INFO */}
        <input
          className="w-full input"
          placeholder="Title"
          value={about.title}
          onChange={(e) => setAbout({ ...about, title: e.target.value })}
        />

        <textarea
          rows={4}
          className="w-full input"
          placeholder="Description"
          value={about.description}
          onChange={(e) =>
            setAbout({ ...about, description: e.target.value })
          }
        />

        {/* MISSION / VISION */}
        <div className="grid lg:grid-cols-2 gap-6">
          <textarea
            rows={3}
            className="w-full input"
            placeholder="Mission"
            value={about.mission}
            onChange={(e) =>
              setAbout({ ...about, mission: e.target.value })
            }
          />
          <textarea
            rows={3}
            className="w-full input"
            placeholder="Vision"
            value={about.vision}
            onChange={(e) =>
              setAbout({ ...about, vision: e.target.value })
            }
          />
        </div>

        {/* MEET THE FOUNDER */}
        <div className="border-t pt-6 space-y-4">
          <h3 className="text-lg font-semibold">Meet the Founder</h3>

          {about?.founder?.image && (
            <img
              src={about?.founder?.image}
              alt="Founder"
              className="w-40 h-40 object-cover rounded-lg"
            />
          )}

          <input type="file" onChange={(e) => setFounderImageFile(e.target.files[0])} />

          <input
            className="w-full input"
            placeholder="Founder Name"
            value={about?.founder?.name}
            onChange={(e) =>
              setAbout({
                ...about,
                founder: { ...about?.founder, name: e.target.value },
              })
            }
          />

          <input
            className="w-full input"
            placeholder="Founder Quote"
            value={about?.founder?.quote}
            onChange={(e) =>
              setAbout({
                ...about,
                founder: { ...about?.founder, quote: e.target.value },
              })
            }
          />

          <input
            className="w-full input"
            placeholder="Founder Role"
            value={about?.founder?.role}
            onChange={(e) =>
              setAbout({
                ...about,
                founder: { ...about?.founder, role: e.target.value },
              })
            }
          />

          <textarea
            rows={4}
            className="w-full input"
            placeholder="Founder Bio"
            value={about?.founder?.bio}
            onChange={(e) =>
              setAbout({
                ...about,
                founder: { ...about.founder, bio: e.target.value },
              })
            }
          />
        </div>

        {/* OUR WORK */}
        <div className="border-t pt-6 space-y-4">
          <h3 className="text-lg font-semibold">Our Work</h3>

          <input
            className="w-full input"
            placeholder="Our Work Title"
            value={about.ourWork.title}
            onChange={(e) =>
              setAbout({
                ...about,
                ourWork: { ...about.ourWork, title: e.target.value },
              })
            }
          />

          <textarea
            rows={3}
            className="w-full input"
            placeholder="Our Work Description"
            value={about.ourWork.description}
            onChange={(e) =>
              setAbout({
                ...about,
                ourWork: {
                  ...about.ourWork,
                  description: e.target.value,
                },
              })
            }
          />

          {about.ourWork.items.map((item, index) => (
            <div key={index} className="border p-4 rounded-lg space-y-2">
              <input
                className="w-full input"
                placeholder="Work Title"
                value={item.title}
                onChange={(e) =>
                  updateWorkItem(index, "title", e.target.value)
                }
              />
              <textarea
                rows={2}
                className="w-full input"
                placeholder="Work Description"
                value={item.description}
                onChange={(e) =>
                  updateWorkItem(index, "description", e.target.value)
                }
              />
              <button
                onClick={() => removeWorkItem(index)}
                className="text-red-600 text-sm"
              >
                Remove
              </button>
            </div>
          ))}

          <button
            onClick={addWorkItem}
            className="text-sm text-[#6a4bc3] font-medium"
          >
            + Add Work Item
          </button>
        </div>

        {/* STATUS */}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={about.isPublished}
            onChange={(e) =>
              setAbout({ ...about, isPublished: e.target.checked })
            }
          />
          Published
        </label>
      </div>
    </div>
  );
}
